/**
 * AuthImage — <img> wrapper that authenticates via dtoken (SEC-002).
 *
 * Replaces the deprecated `<img src={${API}/files?path=…&auth=${token}}>`
 * pattern with `<AuthImage path={storage_path} ... />`. Internally mints a
 * 60-second HMAC dtoken via `useDownloadUrl` and only renders a real
 * image URL once the dtoken resolves. During resolution, the `src` is
 * set to a 1×1 transparent PNG data URI so (a) no broken-image icon
 * flashes, and (b) React does not emit its "An empty string was passed
 * to the src attribute" warning on interim mounts (pre-fix behavior was
 * `src=""` which triggered that warning on every AuthImage mount).
 *
 * Props:
 *   path         — storage_path (required for auth flow)
 *   alt          — alt text
 *   className    — passed through
 *   fallbackSrc  — optional public URL to show while dtoken loads
 *                  (overrides the transparent-pixel default)
 *   ...rest      — any other <img> attributes (onClick, style, etc.)
 */
import React from 'react';
import { useDownloadUrl } from '../lib/dtoken';

// 1×1 transparent PNG — used only during the brief window between mount
// and dtoken resolution. Silences React's empty-src warning without any
// visible flash (fully transparent, matches the container's background).
const TRANSPARENT_1PX =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

export function AuthImage({ path, alt = '', fallbackSrc = '', ...rest }) {
  const { url } = useDownloadUrl(path);
  return <img src={url || fallbackSrc || TRANSPARENT_1PX} alt={alt} {...rest} />;
}

export default AuthImage;
